import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Save, Download, RefreshCw, 
  Sparkles, CheckCircle, AlertCircle, Trophy, Clock, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import ProjectExplorer from '@/components/ProjectExplorer';
import { DynamicField } from '@/components/ProjectFormFields';
import confetti from 'canvas-confetti';
import { useTheme } from '@/contexts/ThemeContext';
import jsPDF from 'jspdf';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Deadline countdown component
const DeadlineCountdown = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState(null);
  
  useEffect(() => {
    if (!deadline) return;
    
    const calculateTimeLeft = () => {
      const now = new Date();
      const deadlineDate = new Date(deadline);
      const diff = deadlineDate - now;
      
      if (diff <= 0) {
        return { expired: true };
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      
      return { days, hours, minutes, expired: false };
    };
    
    setTimeLeft(calculateTimeLeft());
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000); // Update every minute
    
    return () => clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);
  
  if (!deadline || !timeLeft) return null;
  
  // Determine urgency level and colors
  const getUrgencyStyle = () => {
    if (timeLeft.expired) {
      return { bg: 'bg-red-100', border: 'border-red-300', text: 'text-red-700', icon: '🚨' };
    }
    if (timeLeft.days <= 3) {
      return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', icon: '⚠️' };
    }
    if (timeLeft.days <= 7) {
      return { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-600', icon: '⏰' };
    }
    if (timeLeft.days <= 14) {
      return { bg: 'bg-yellow-50', border: 'border-yellow-200', text: 'text-yellow-700', icon: '📅' };
    }
    return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: '✅' };
  };
  
  const style = getUrgencyStyle();
  const deadlineDate = new Date(deadline);
  
  return (
    <div className={`${style.bg} ${style.border} border-2 rounded-2xl p-4 mb-6`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <span className="text-2xl">{style.icon}</span>
          <div>
            <p className={`font-semibold ${style.text}`}>
              {timeLeft.expired ? 'Deadline Passed!' : 'Project Deadline'}
            </p>
            <p className="text-sm text-gray-600">
              {deadlineDate.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </p>
          </div>
        </div>
        
        {!timeLeft.expired && (
          <div className="flex gap-3 text-center">
            <div className={`${style.bg} px-3 py-2 rounded-xl`}>
              <p className={`text-2xl font-bold ${style.text}`}>{timeLeft.days}</p>
              <p className="text-xs text-gray-500">days</p>
            </div>
            <div className={`${style.bg} px-3 py-2 rounded-xl`}>
              <p className={`text-2xl font-bold ${style.text}`}>{timeLeft.hours}</p>
              <p className="text-xs text-gray-500">hours</p>
            </div>
            <div className={`${style.bg} px-3 py-2 rounded-xl`}>
              <p className={`text-2xl font-bold ${style.text}`}>{timeLeft.minutes}</p>
              <p className="text-xs text-gray-500">mins</p>
            </div>
          </div>
        )}
        
        {timeLeft.expired && (
          <p className={`${style.text} font-semibold`}>
            Please submit as soon as possible!
          </p>
        )}
      </div>
      
      {!timeLeft.expired && timeLeft.days <= 7 && (
        <p className={`text-sm ${style.text} mt-2 text-center`}>
          {timeLeft.days <= 3 
            ? "⚡ Finish strong! You're almost there!" 
            : "📝 Keep working on your project!"}
        </p>
      )}
    </div>
  );
};

const SummerProjectPage = ({ user }) => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [myProject, setMyProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showSwitchConfirm, setShowSwitchConfirm] = useState(false);
  const [progressPercent, setProgressPercent] = useState(0);
  const [canSwitch, setCanSwitch] = useState(false);
  const [viewMode, setViewMode] = useState(null); // null = summary, 'view' = read-only browse, 'edit' = editing
  const [deadline, setDeadline] = useState(null);

  useEffect(() => {
    fetchMyProject();
    fetchDeadline();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDeadline = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/settings/project-deadline`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeadline(data.deadline);
      }
    } catch (error) {
      console.error('Error fetching deadline:', error);
    }
  };

  const fetchMyProject = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/projects/my-project`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data) {
          setMyProject(data);
          setCurrentStep(data.submission?.current_step || 0);
          setResponses(data.submission?.responses || {});
          setProgressPercent(data.progress_percent || 0);
          setCanSwitch(data.can_switch || false);
        }
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSelected = (projectId) => {
    fetchMyProject();
  };

  const saveProgress = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/projects/save-progress?current_step=${currentStep}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(responses)
      });
      toast.success('Progress saved! ✓');
    } catch (error) {
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const submitProject = async () => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/projects/submit`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 }
      });
      
      toast.success('🎉 Congratulations! You earned 250 points!');
      fetchMyProject();
    } catch (error) {
      toast.error('Failed to submit project');
    }
  };

  const switchProject = async () => {
    setShowSwitchConfirm(true);
  };

  const confirmSwitchProject = async () => {
    // Reset project and go back to explorer
    try {
      const token = localStorage.getItem('token');
      // Delete current submission to allow reselection
      await fetch(`${API_URL}/api/projects/my-project`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyProject(null);
      setResponses({});
      setCurrentStep(0);
      setShowSwitchConfirm(false);
      toast.info('Project reset. Choose a new one!');
    } catch (error) {
      // If delete endpoint doesn't exist, just reset locally
      setMyProject(null);
      setResponses({});
      setCurrentStep(0);
      setShowSwitchConfirm(false);
    }
  };

  const downloadPDF = () => {
    const doc = new jsPDF();
    const project = myProject?.project_details;
    const userName = user?.name || 'Participant';
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(249, 115, 22); // Orange
    doc.text(`${project?.icon || '📋'} ${project?.title || 'Summer Project'}`, 20, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`By: ${userName}`, 20, 30);
    doc.text(`Completed: ${new Date().toLocaleDateString()}`, 20, 38);
    
    let yPosition = 55;
    
    // Add each step's responses
    project?.steps?.forEach((step, stepIndex) => {
      if (yPosition > 260) {
        doc.addPage();
        yPosition = 20;
      }
      
      // Step title
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text(`${stepIndex + 1}. ${step.title}`, 20, yPosition);
      yPosition += 10;
      
      // Fields
      step.fields?.forEach(field => {
        if (field.type === 'calculated') return; // Skip calculated fields
        
        const value = responses[field.name];
        if (!value) return;
        
        if (yPosition > 270) {
          doc.addPage();
          yPosition = 20;
        }
        
        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        
        // Handle different field types
        if (Array.isArray(value)) {
          // Table data
          doc.text(field.label + ':', 25, yPosition);
          yPosition += 6;
          value.forEach((row, i) => {
            const rowText = Object.entries(row)
              .filter(([k, v]) => v)
              .map(([k, v]) => `${v}`)
              .join(' | ');
            if (rowText) {
              doc.setTextColor(50, 50, 50);
              doc.text(`  • ${rowText}`, 30, yPosition);
              yPosition += 5;
            }
          });
          yPosition += 3;
        } else if (typeof value === 'string' && value.length > 0) {
          doc.text(field.label + ':', 25, yPosition);
          yPosition += 6;
          
          // Word wrap long text
          doc.setTextColor(50, 50, 50);
          const lines = doc.splitTextToSize(value, 160);
          lines.forEach(line => {
            if (yPosition > 270) {
              doc.addPage();
              yPosition = 20;
            }
            doc.text(line, 30, yPosition);
            yPosition += 5;
          });
          yPosition += 3;
        }
      });
      
      yPosition += 8;
    });
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text('Generated from SYEP Summer Project Platform', 20, 285);
    
    doc.save(`SYEP_${project?.title?.replace(/\s+/g, '_')}_${userName.replace(/\s+/g, '_')}.pdf`);
    toast.success('PDF downloaded! 📄');
  };

  const handleInputChange = (fieldName, value) => {
    setResponses(prev => ({
      ...prev,
      [fieldName]: value
    }));
    // Clear validation error when user starts typing
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  // Validate current step's required fields
  const validateCurrentStep = () => {
    const currentFields = projectDetails?.steps?.[currentStep]?.fields || [];
    const errors = {};
    let isValid = true;

    currentFields.forEach(field => {
      // Skip calculated fields
      if (field.type === 'calculated') return;
      
      const value = responses[field.name];
      
      if (field.type === 'table') {
        // Check if table has minimum rows with data
        const minRows = field.min_rows || 1;
        const rows = value || [];
        const filledRows = rows.filter(row => {
          // Check if any non-label column has data
          return field.columns.some(col => 
            col.type !== 'auto_number' && col.type !== 'label' && row[col.key]?.toString().trim()
          );
        });
        if (filledRows.length < minRows) {
          errors[field.name] = true;
          isValid = false;
        }
      } else if (field.type === 'checklist') {
        if (!value || value.length === 0) {
          errors[field.name] = true;
          isValid = false;
        }
      } else if (field.type === 'group') {
        // Handle grouped fields
        field.fields?.forEach(subField => {
          const subValue = responses[subField.name]?.toString().trim();
          if (!subValue) {
            errors[subField.name] = true;
            isValid = false;
          }
        });
      } else {
        const strValue = value?.toString().trim() || '';
        if (!strValue) {
          errors[field.name] = true;
          isValid = false;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  // Check if current step is complete
  const isCurrentStepComplete = () => {
    const currentFields = projectDetails?.steps?.[currentStep]?.fields || [];
    return currentFields.every(field => {
      if (field.type === 'calculated') return true;
      
      const value = responses[field.name];
      
      if (field.type === 'table') {
        const minRows = field.min_rows || 1;
        const rows = value || [];
        return rows.filter(row => 
          field.columns.some(col => 
            col.type !== 'auto_number' && col.type !== 'label' && row[col.key]?.toString().trim()
          )
        ).length >= minRows;
      } else if (field.type === 'checklist') {
        return value && value.length > 0;
      } else if (field.type === 'group') {
        return field.fields?.every(subField => responses[subField.name]?.toString().trim());
      } else {
        return value?.toString().trim().length > 0;
      }
    });
  };

  // Handle next step with validation
  const handleNextStep = () => {
    if (validateCurrentStep()) {
      saveProgress();
      setCurrentStep(prev => prev + 1);
      setValidationErrors({});
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      toast.error('Please complete all fields before continuing');
    }
  };

  // Handle submit with validation
  const handleSubmitProject = () => {
    if (validateCurrentStep()) {
      submitProject();
    } else {
      toast.error('Please complete all fields before submitting');
    }
  };

  // Helper function to render field values in view mode
  const renderFieldValue = (field, value, allResponses) => {
    if (!value && field.type !== 'calculated') {
      return <span className="text-gray-400 italic">Not filled in</span>;
    }

    switch (field.type) {
      case 'table':
        if (!Array.isArray(value) || value.length === 0) {
          return <span className="text-gray-400 italic">No entries</span>;
        }
        return (
          <div className="space-y-2">
            {value.map((row, i) => {
              const rowContent = field.columns
                .filter(col => col.type !== 'auto_number' && col.type !== 'label')
                .map(col => {
                  const cellValue = row[col.key];
                  if (!cellValue) return null;
                  if (col.type === 'currency') return `$${cellValue}`;
                  return cellValue;
                })
                .filter(Boolean)
                .join(' • ');
              
              return rowContent ? (
                <div key={i} className="flex items-start gap-2">
                  <span className="w-6 h-6 bg-orange-100 rounded-full flex items-center justify-center text-xs font-bold text-orange-600 flex-shrink-0">
                    {i + 1}
                  </span>
                  <span>{rowContent}</span>
                </div>
              ) : null;
            })}
          </div>
        );
      
      case 'checklist':
        if (!Array.isArray(value) || value.length === 0) {
          return <span className="text-gray-400 italic">None selected</span>;
        }
        return (
          <div className="flex flex-wrap gap-2">
            {value.map((item, i) => (
              <span key={i} className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                ✓ {item}
              </span>
            ))}
          </div>
        );
      
      case 'currency':
        return <span className="text-lg font-semibold text-green-600">${value}</span>;
      
      case 'calculated':
        // Calculate the value
        let calcValue = 0;
        if (field.formula?.startsWith('sum:')) {
          const path = field.formula.replace('sum:', '').split('.');
          const fieldName = path[0];
          const columnKey = path[1];
          const tableData = allResponses[fieldName] || [];
          calcValue = tableData.reduce((sum, row) => sum + (parseFloat(row[columnKey]) || 0), 0);
        } else if (field.formula) {
          const parts = field.formula.split(/([+\-*/])/);
          if (parts.length === 3) {
            const val1 = parseFloat(allResponses[parts[0].trim()]) || 0;
            const val2 = parseFloat(allResponses[parts[2].trim()]) || 0;
            switch (parts[1].trim()) {
              case '+': calcValue = val1 + val2; break;
              case '-': calcValue = val1 - val2; break;
              case '*': calcValue = val1 * val2; break;
              case '/': calcValue = val2 !== 0 ? val1 / val2 : 0; break;
              default: calcValue = 0;
            }
          }
        }
        return (
          <span className={`text-xl font-bold ${
            field.display === 'currency_highlight' 
              ? calcValue >= 0 ? 'text-green-600' : 'text-red-600'
              : 'text-gray-800'
          }`}>
            {field.display?.includes('currency') ? `$${calcValue.toFixed(2)}` : Math.round(calcValue)}
          </span>
        );
      
      case 'select':
      case 'toggle':
        return <span className="font-medium">{value}</span>;
      
      case 'date':
        return <span>{new Date(value).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>;
      
      default:
        return <span className="whitespace-pre-wrap">{value}</span>;
    }
  };

  const projectDetails = myProject?.project_details;
  const currentStepData = projectDetails?.steps?.[currentStep];
  const totalSteps = projectDetails?.steps?.length || 0;
  const submission = myProject?.submission;
  const isSubmitted = submission?.status === 'submitted' || submission?.status === 'approved';

  if (loading) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
        <Navigation user={user} />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin w-12 h-12 border-4 border-orange-500 border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  // Show Project Explorer if no project selected
  if (!myProject) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
        <Navigation user={user} />
        <div className="pt-20">
          <ProjectExplorer 
            onSelectProject={handleProjectSelected}
            onBack={() => navigate('/dashboard')}
          />
        </div>
      </div>
    );
  }

  // Submitted View - With View/Edit Options
  if (isSubmitted) {
    // If in view or edit mode, show the project browser
    if (viewMode === 'view' || viewMode === 'edit') {
      return (
        <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
          <Navigation user={user} />
          
          <div className="pt-20 max-w-3xl mx-auto p-4 pb-32">
            {/* Header with Back Button */}
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() => {
                  setViewMode(null);
                  setCurrentStep(0);
                }}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Summary
              </button>
              
              {viewMode === 'view' && (
                <Button
                  onClick={() => setViewMode('edit')}
                  variant="outline"
                  className="rounded-full"
                >
                  ✏️ Edit Project
                </Button>
              )}
              
              {viewMode === 'edit' && (
                <Button
                  onClick={() => {
                    saveProgress();
                    setViewMode('view');
                  }}
                  className="rounded-full bg-green-500 hover:bg-green-600"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </Button>
              )}
            </div>

            {/* Project Header */}
            <div className="text-center mb-6">
              <span className="text-5xl mb-2 block">{projectDetails?.icon}</span>
              <h1 className="text-2xl font-bold">{projectDetails?.title}</h1>
              <div className="flex items-center justify-center gap-2 mt-2">
                <span className={`px-3 py-1 rounded-full text-sm ${
                  viewMode === 'edit' 
                    ? 'bg-orange-100 text-orange-700' 
                    : 'bg-green-100 text-green-700'
                }`}>
                  {viewMode === 'edit' ? '✏️ Editing Mode' : '👁️ Viewing Mode'}
                </span>
              </div>
              <p className="text-gray-600 text-sm mt-2">
                Step {currentStep + 1} of {totalSteps}
              </p>
            </div>

            {/* Step Content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <Card className="p-6 rounded-3xl">
                  {/* Step Header */}
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center font-bold text-orange-600">
                      {currentStep + 1}
                    </div>
                    <div>
                      <h2 className="font-bold text-lg">{currentStepData?.title}</h2>
                      {currentStepData?.description && (
                        <p className="text-sm text-gray-500">{currentStepData.description}</p>
                      )}
                    </div>
                  </div>

                  {/* Fields - View or Edit Mode */}
                  <div className="space-y-6">
                    {currentStepData?.fields?.map((field) => (
                      <div key={field.name}>
                        {viewMode === 'edit' ? (
                          // Edit Mode - Show editable fields
                          field.type === 'group' ? (
                            <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                              <h3 className="font-semibold text-gray-700">{field.label}</h3>
                              {field.fields?.map(subField => (
                                <DynamicField
                                  key={subField.name}
                                  field={subField}
                                  value={responses[subField.name]}
                                  onChange={(val) => handleInputChange(subField.name, val)}
                                  validationError={validationErrors[subField.name]}
                                  allResponses={responses}
                                />
                              ))}
                            </div>
                          ) : (
                            <DynamicField
                              field={field}
                              value={responses[field.name]}
                              onChange={(val) => handleInputChange(field.name, val)}
                              validationError={validationErrors[field.name]}
                              allResponses={responses}
                            />
                          )
                        ) : (
                          // View Mode - Show read-only display
                          <div className="mb-4">
                            <p className="font-semibold text-gray-700 mb-2">{field.label}</p>
                            <div className="bg-gray-50 rounded-xl p-4">
                              {renderFieldValue(field, responses[field.name], responses)}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Navigation Buttons */}
                  <div className="flex gap-3 mt-8">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCurrentStep(prev => prev - 1);
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }}
                      disabled={currentStep === 0}
                      className="flex-1 h-12 rounded-full"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Previous
                    </Button>

                    {viewMode === 'edit' && (
                      <Button
                        variant="outline"
                        onClick={saveProgress}
                        disabled={saving}
                        className="h-12 px-6 rounded-full"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {saving ? 'Saving...' : 'Save'}
                      </Button>
                    )}

                    <Button
                      onClick={() => {
                        if (currentStep < totalSteps - 1) {
                          setCurrentStep(prev => prev + 1);
                          window.scrollTo({ top: 0, behavior: 'smooth' });
                        }
                      }}
                      disabled={currentStep >= totalSteps - 1}
                      className="flex-1 h-12 rounded-full bg-orange-500 hover:bg-orange-600"
                    >
                      Next
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Step Navigation Dots */}
            <div className="flex justify-center gap-2 mt-6 flex-wrap">
              {projectDetails?.steps?.map((step, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentStep(idx);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`w-8 h-8 rounded-full transition-all flex items-center justify-center text-xs font-bold ${
                    idx === currentStep
                      ? 'bg-orange-500 text-white scale-110'
                      : 'bg-green-500 text-white'
                  }`}
                  title={step.title}
                >
                  {idx + 1}
                </button>
              ))}
            </div>
          </div>
        </div>
      );
    }

    // Default: Show Summary View with options
    return (
      <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
        <Navigation user={user} />
        <div className="pt-20 max-w-2xl mx-auto p-4">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="py-8"
          >
            <div className="text-center mb-8">
              <Trophy className="w-20 h-20 text-yellow-500 mx-auto mb-4" />
              <h1 className="text-3xl font-bold mb-2">🎉 Project Complete!</h1>
              <p className="text-xl text-gray-600 mb-1">
                {projectDetails?.icon} {projectDetails?.title}
              </p>
              <p className="text-green-600 font-semibold">
                You earned 250 points!
              </p>
            </div>

            <Card className="p-6 rounded-3xl mb-6 bg-gradient-to-r from-orange-100 to-yellow-100">
              <p className="text-gray-700 text-center">
                Your project is part of your SYEP portfolio. You can view it anytime, 
                make updates, or download it as a PDF to keep forever!
              </p>
            </Card>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                onClick={() => {
                  setViewMode('view');
                  setCurrentStep(0);
                }}
                className="w-full h-14 text-lg rounded-2xl bg-blue-500 hover:bg-blue-600"
              >
                👁️ View My Project
              </Button>

              <Button
                onClick={() => {
                  setViewMode('edit');
                  setCurrentStep(0);
                }}
                variant="outline"
                className="w-full h-14 text-lg rounded-2xl border-2 border-orange-500 text-orange-600 hover:bg-orange-50"
              >
                ✏️ Edit My Project
              </Button>

              <Button
                onClick={downloadPDF}
                className="w-full h-14 text-lg rounded-2xl bg-orange-500 hover:bg-orange-600"
              >
                <Download className="w-5 h-5 mr-2" />
                Download as PDF
              </Button>

              <Button
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="w-full h-12 rounded-2xl text-gray-600"
              >
                Back to Dashboard
              </Button>
            </div>

            {/* Quick Stats */}
            <Card className="p-4 rounded-2xl mt-6 bg-white">
              <h3 className="font-semibold text-center mb-3">📊 Project Stats</h3>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-orange-500">{totalSteps}</p>
                  <p className="text-sm text-gray-500">Steps Completed</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-500">250</p>
                  <p className="text-sm text-gray-500">Points Earned</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-500">
                    {submission?.submitted_at ? new Date(submission.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '✓'}
                  </p>
                  <p className="text-sm text-gray-500">Submitted</p>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  // Active Project View
  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white">
      <Navigation user={user} />
      
      <div className="pt-20 max-w-3xl mx-auto p-4 pb-32">
        {/* Deadline Countdown */}
        <DeadlineCountdown deadline={deadline} />

        {/* Project Header */}
        <div className="text-center mb-6">
          <span className="text-5xl mb-2 block">{projectDetails?.icon}</span>
          <h1 className="text-2xl font-bold">{projectDetails?.title}</h1>
          <p className="text-gray-600 text-sm mt-1">
            Step {currentStep + 1} of {totalSteps}
          </p>
          
          {/* Progress Bar */}
          <div className="mt-4 px-4">
            <Progress 
              value={((currentStep + 1) / totalSteps) * 100} 
              className="h-3 bg-gray-200"
            />
            <p className="text-sm text-gray-500 mt-1">
              {Math.round(((currentStep + 1) / totalSteps) * 100)}% complete
            </p>
          </div>

          {/* Switch Project Option */}
          {canSwitch && (
            <Button
              variant="ghost"
              size="sm"
              onClick={switchProject}
              className="mt-3 text-gray-500 hover:text-orange-500"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Switch to different project
            </Button>
          )}
        </div>

        {/* Switch Confirmation Modal */}
        <AnimatePresence>
          {showSwitchConfirm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            >
              <motion.div
                initial={{ scale: 0.9 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.9 }}
                className="bg-white rounded-3xl p-6 max-w-md w-full"
              >
                <AlertCircle className="w-12 h-12 text-orange-500 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-center mb-2">Switch Projects?</h3>
                <p className="text-gray-600 text-center mb-4">
                  Your current progress will be lost and your 20 selection points will be reset. 
                  Make sure you want to start fresh with a different project.
                </p>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSwitchConfirm(false)}
                    className="flex-1 h-12 rounded-xl"
                  >
                    Keep Current
                  </Button>
                  <Button
                    onClick={confirmSwitchProject}
                    className="flex-1 h-12 rounded-xl bg-orange-500 hover:bg-orange-600"
                  >
                    Switch Project
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
          >
            <Card className="p-6 rounded-3xl">
              {/* Step Header */}
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center font-bold text-orange-600">
                  {currentStep + 1}
                </div>
                <div>
                  <h2 className="font-bold text-lg">{currentStepData?.title}</h2>
                  {currentStepData?.description && (
                    <p className="text-sm text-gray-500">{currentStepData.description}</p>
                  )}
                </div>
              </div>
              
              {/* Required Fields Notice */}
              <p className="text-sm text-orange-600 mb-6 flex items-center gap-1">
                <span className="text-red-500">*</span> All fields are required to continue
              </p>

              {/* Dynamic Fields */}
              <div className="space-y-6">
                {currentStepData?.fields?.map((field) => (
                  <div key={field.name}>
                    {field.type === 'group' ? (
                      // Render grouped fields (like STAR stories)
                      <div className="space-y-4 p-4 bg-gray-50 rounded-xl">
                        <h3 className="font-semibold text-gray-700">{field.label}</h3>
                        {field.fields?.map(subField => (
                          <DynamicField
                            key={subField.name}
                            field={subField}
                            value={responses[subField.name]}
                            onChange={(val) => handleInputChange(subField.name, val)}
                            validationError={validationErrors[subField.name]}
                            allResponses={responses}
                          />
                        ))}
                      </div>
                    ) : (
                      <DynamicField
                        field={field}
                        value={responses[field.name]}
                        onChange={(val) => handleInputChange(field.name, val)}
                        validationError={validationErrors[field.name]}
                        allResponses={responses}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-3 mt-8">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCurrentStep(prev => prev - 1);
                    setValidationErrors({});
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  disabled={currentStep === 0}
                  className="flex-1 h-12 rounded-full"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Previous
                </Button>

                <Button
                  variant="outline"
                  onClick={saveProgress}
                  disabled={saving}
                  className="h-12 px-6 rounded-full"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>

                {currentStep < totalSteps - 1 ? (
                  <Button
                    onClick={handleNextStep}
                    className={`flex-1 h-12 rounded-full ${
                      isCurrentStepComplete() 
                        ? 'bg-orange-500 hover:bg-orange-600' 
                        : 'bg-orange-300'
                    }`}
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmitProject}
                    className={`flex-1 h-12 rounded-full ${
                      isCurrentStepComplete() 
                        ? 'bg-green-500 hover:bg-green-600' 
                        : 'bg-green-300'
                    }`}
                    data-testid="submit-project-button"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Complete Project
                  </Button>
                )}
              </div>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Step Navigation Dots */}
        <div className="flex justify-center gap-2 mt-6 flex-wrap">
          {projectDetails?.steps?.map((step, idx) => (
            <button
              key={idx}
              onClick={() => {
                if (idx <= currentStep) {
                  setCurrentStep(idx);
                  setValidationErrors({});
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                } else {
                  toast.info('Complete current step first');
                }
              }}
              className={`w-8 h-8 rounded-full transition-all flex items-center justify-center text-xs font-bold ${
                idx === currentStep
                  ? 'bg-orange-500 text-white scale-110'
                  : idx < currentStep
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
              title={step.title}
            >
              {idx < currentStep ? <CheckCircle className="w-4 h-4" /> : idx + 1}
            </button>
          ))}
        </div>

        {/* Encouragement */}
        <p className="text-center text-gray-500 text-sm mt-4">
          {currentStep === 0 && "Let's start your journey! Take your time."}
          {currentStep > 0 && currentStep < totalSteps - 1 && "Great progress! Keep going!"}
          {currentStep === totalSteps - 1 && "Almost there! Review and submit your project."}
        </p>
      </div>
    </div>
  );
};

export default SummerProjectPage;
